import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Tag, User, Search, Plus } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { mockData } from '../data/mockData';

export const Noticias = () => {
  const [news, setNews] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const navigate = useNavigate();

  const categories = ['all', 'Convocatoria', 'Evento', 'Noticia'];

  useEffect(() => {
    fetch(`/data/news.json?t=${Date.now()}`, {
      cache: 'no-cache',
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      }
    })
      .then(res => res.json())
      .then(data => setNews(data))
      .catch(() => setNews([]));
  }, []);

  const getCategoryColor = (category) => {
    switch (category) {
      case 'Convocatoria':
        return 'bg-blue-100 text-blue-800';
      case 'Evento':
        return 'bg-green-100 text-green-800';
      case 'Noticia':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const filteredNews = news.filter(item => {
    const matchesSearch = item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const featuredNews = filteredNews.filter(item => item.featured);
  const regularNews = filteredNews.filter(item => !item.featured);

  // Función para navegar a detalle de noticia
  const viewNewsDetail = (newsItem) => {
    navigate(`/noticias/${newsItem.id}`);
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1504711434969-e33886168f5c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80"
            alt="News and events"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-burgundy-50/65 to-white/75"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-burgundy-900 mb-6">
              Noticias y Eventos
            </h1>
            <p className="text-xl text-burgundy-700 mb-8 max-w-3xl mx-auto">
              Mantente informado sobre convocatorias, eventos académicos y
              logros de las universidades promotoras de la salud
            </p>
          </div>
        </div>
      </section>

      {/* Search and Admin Section */}
      <section className="py-8 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex flex-col md:flex-row gap-4 flex-1">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Buscar noticias y eventos..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className="flex gap-2">
                {categories.map(category => (
                  <Button
                    key={category}
                    variant={selectedCategory === category ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSelectedCategory(category)}
                    className={selectedCategory === category ?
                      "bg-burgundy-700 hover:bg-burgundy-800" :
                      "border-burgundy-300 text-burgundy-700 hover:bg-burgundy-50"
                    }
                  >
                    {category === 'all' ? 'Todos' : category}
                  </Button>
                ))}
              </div>
            </div>

            {/* Admin Button - Mock for content management 
            <Button className="bg-burgundy-700 hover:bg-burgundy-800 text-white">
              <Plus className="h-4 w-4 mr-2" />
              Agregar Contenido
            </Button>*/}
          </div>
        </div>
      </section>

      {/* Featured News */}
      {featuredNews.length > 0 && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl md:text-3xl font-bold text-burgundy-900 mb-8">
              Destacados
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {featuredNews.map((item) => (
                <Card key={item.id} className="overflow-hidden hover:shadow-lg transition-shadow duration-300">
                  <div className="h-48 bg-gradient-to-r from-burgundy-100 to-burgundy-200 flex items-center justify-center">
                    <div className="text-center">
                      <Tag className="h-12 w-12 text-burgundy-700 mx-auto mb-2" />
                      <Badge className={getCategoryColor(item.category)}>
                        {item.category}
                      </Badge>
                    </div>
                  </div>
                  <CardHeader>
                    <div className="flex items-center justify-between text-sm text-gray-500 mb-2">
                      <div className="flex items-center space-x-2">
                        <Calendar className="h-4 w-4" />
                        <span>{formatDate(item.date)}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <User className="h-4 w-4" />
                        <span>Administrador</span>
                      </div>
                    </div>
                    <CardTitle 
                      className="text-xl text-burgundy-800 hover:text-burgundy-900 cursor-pointer"
                      onClick={() => viewNewsDetail(item)}
                    >
                      {item.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 mb-4">{item.excerpt}</p>
                    <Button
                      variant="outline"
                      className="border-burgundy-300 text-burgundy-700 hover:bg-burgundy-50"
                      onClick={() => viewNewsDetail(item)}
                    >
                      Leer más
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Regular News */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-burgundy-900 mb-8">
            Todas las Noticias
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {regularNews.map((item) => (
              <Card key={item.id} className="hover:shadow-lg transition-shadow duration-300">
                <CardHeader>
                  <div className="flex items-center justify-between mb-2">
                    <Badge className={getCategoryColor(item.category)}>
                      {item.category}
                    </Badge>
                    <div className="flex items-center text-sm text-gray-500">
                      <Calendar className="h-4 w-4 mr-1" />
                      <span>{formatDate(item.date)}</span>
                    </div>
                  </div>
                  <CardTitle 
                    className="text-lg text-burgundy-800 hover:text-burgundy-900 cursor-pointer"
                    onClick={() => viewNewsDetail(item)}
                  >
                    {item.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-4 line-clamp-3">{item.excerpt}</p>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-burgundy-300 text-burgundy-700 hover:bg-burgundy-50"
                    onClick={() => viewNewsDetail(item)}
                  >
                    Leer más
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {filteredNews.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-600 text-lg">
                No se encontraron noticias con los criterios seleccionados
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Upcoming Events Section */}
      <section className="py-16 bg-burgundy-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Próximos Eventos
            </h2>
            <p className="text-lg text-burgundy-100 max-w-3xl mx-auto">
              No te pierdas los webinars, talleres y congresos internacionales
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="bg-burgundy-800 border-burgundy-700">
              <CardHeader>
                <CardTitle className="text-white">III Congreso Internacional UPS</CardTitle>
                <p className="text-burgundy-200">15-17 Marzo 2025</p>
              </CardHeader>
              <CardContent>
                <p className="text-burgundy-200 mb-4">
                  Encuentro global de universidades promotoras de la salud
                </p>
                <Button className="bg-white text-burgundy-900 hover:bg-gray-100">
                  Más información
                </Button>
              </CardContent>
            </Card>

            <Card className="bg-burgundy-800 border-burgundy-700">
              <CardHeader>
                <CardTitle className="text-white">Taller de Evaluadores</CardTitle>
                <p className="text-burgundy-200">10 Febrero 2025</p>
              </CardHeader>
              <CardContent>
                <p className="text-burgundy-200 mb-4">
                  Capacitación para nuevos evaluadores externos
                </p>
                <Button className="bg-white text-burgundy-900 hover:bg-gray-100">
                  Inscribirse
                </Button>
              </CardContent>
            </Card>

            <Card className="bg-burgundy-800 border-burgundy-700">
              <CardHeader>
                <CardTitle className="text-white">Webinar Mensual</CardTitle>
                <p className="text-burgundy-200">Último viernes de cada mes</p>
              </CardHeader>
              <CardContent>
                <p className="text-burgundy-200 mb-4">
                  Serie de webinars sobre implementación del modelo
                </p>
                <Button className="bg-white text-burgundy-900 hover:bg-gray-100">
                  Ver calendario
                </Button>
              </CardContent>
            </Card>


          </div>
          <hr className='mt-12' />
        </div>

      </section>

    </div>
  );
};